<template>
  <div class="protected-avatar isolate" :style="{ width: size + 'px', height: size + 'px' }">
    <!-- isolate: o z-index do escudo vale só dentro do avatar; sem isso ele furava overlays
         da página (o conteúdo do DashboardLayout tem stacking context próprio, z-index 0). -->
    <canvas v-show="ready" ref="canvasRef" class="protected-avatar__canvas" />
    <div v-if="!ready" class="protected-avatar__fallback">
      <slot />
    </div>
    <div class="protected-avatar__shield" @contextmenu.prevent @dragstart.prevent />
  </div>
</template>

<script setup lang="ts">
import { ref, watch, onBeforeUnmount, nextTick } from 'vue'

const props = withDefaults(
  defineProps<{
    src?: string | null
    size?: number
  }>(),
  { size: 40 }
)

const canvasRef = ref<HTMLCanvasElement | null>(null)
const ready = ref(false)

let currentBitmap: ImageBitmap | null = null
let loadGeneration = 0
let abortController: AbortController | null = null

function draw() {
  const canvas = canvasRef.value
  if (!currentBitmap || !canvas) return

  const dpr = window.devicePixelRatio || 1
  canvas.width = props.size * dpr
  canvas.height = props.size * dpr

  const ctx = canvas.getContext('2d')
  if (!ctx) return

  ctx.clearRect(0, 0, canvas.width, canvas.height)
  ctx.save()

  ctx.beginPath()
  ctx.arc(canvas.width / 2, canvas.height / 2, canvas.width / 2, 0, Math.PI * 2)
  ctx.closePath()
  ctx.clip()

  const iw = currentBitmap.width
  const ih = currentBitmap.height
  const scale = Math.max(canvas.width / iw, canvas.height / ih)
  const x = (canvas.width - iw * scale) / 2
  const y = (canvas.height - ih * scale) / 2
  ctx.drawImage(currentBitmap, x, y, iw * scale, ih * scale)

  ctx.restore()

  canvas.toDataURL = () => ''
  canvas.toBlob = (cb: BlobCallback) => cb(null)
}

function dispose() {
  ++loadGeneration
  if (abortController) {
    abortController.abort()
    abortController = null
  }
  if (currentBitmap) {
    currentBitmap.close()
    currentBitmap = null
  }
  ready.value = false
}

async function fetchAsImageBitmap(url: string, signal: AbortSignal): Promise<ImageBitmap> {
  const response = await fetch(url, { signal })
  if (!response.ok) throw new Error(`${response.status}`)
  const blob = await response.blob()
  return createImageBitmap(blob)
}

function loadViaImage(url: string): Promise<ImageBitmap> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = () => {
      createImageBitmap(img).then(resolve, reject)
    }
    img.onerror = reject
    img.src = url
  })
}

async function load(url: string) {
  dispose()
  const generation = ++loadGeneration
  abortController = new AbortController()

  try {
    let bitmap: ImageBitmap
    try {
      bitmap = await fetchAsImageBitmap(url, abortController.signal)
    } catch {
      if (generation !== loadGeneration) return
      // Fallback: legacy Azure Blob URLs may not have CORS configured
      bitmap = await loadViaImage(url)
    }
    if (generation !== loadGeneration) {
      bitmap.close()
      return
    }
    currentBitmap = bitmap
    ready.value = true
    await nextTick()
    draw()
  } catch {
    if (generation === loadGeneration) ready.value = false
  }
}

watch(
  () => props.src,
  (url) => {
    if (url) load(url)
    else dispose()
  },
  { immediate: true }
)

watch(
  () => props.size,
  () => {
    if (currentBitmap) {
      nextTick().then(draw)
    }
  }
)

onBeforeUnmount(() => {
  ++loadGeneration
  dispose()
})
</script>

<style scoped>
.protected-avatar {
  position: relative;
  flex-shrink: 0;
}

.protected-avatar__canvas {
  width: 100%;
  height: 100%;
  display: block;
}

.protected-avatar__fallback {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  overflow: hidden;
}

.protected-avatar__shield {
  position: absolute;
  inset: 0;
  z-index: 1;
  background: transparent;
}
</style>
